import { PencilIcon } from "@primer/octicons-react";
import { Box, Button, Label, SelectPanel } from "@primer/react";
import { useMemo, useState } from "react";
import { sf_hinhthucmiengiam_vm } from "../../models/response/category/sf_hinhthucmiengiam";
import { RootState } from "../../state/reducers";
import { useSelector } from "react-redux";

type SelectBoxMultipleHinhThucMienGiamProps = {
  isReadonly?: boolean;
  value?: number[];
  onValueChanged: (ids: number[]) => void;
  className?: string;
  preText?: string;
  width?: string | number;
  stylingMode?: "outlined" | "filled" | "underlined";
  isShowClearBtn?: boolean;
  maxWidth?: any;
  sf_hinhthucmiengiams: sf_hinhthucmiengiam_vm[];
};

const SelectBoxMultipleHinhThucMienGiam = (
  props: SelectBoxMultipleHinhThucMienGiamProps
) => {
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const { dm_coso_id } = useSelector((state: RootState) => state.common);

  const removeVietnameseTones = (str: string) => {
    return str
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/đ/g, "d")
      .replace(/Đ/g, "D");
  };

  // Tạo danh sách items với divider giữa các groups
  const allSelectableItems = useMemo(() => {
    const filteredData = props.sf_hinhthucmiengiams.filter(x => x.dm_coso_id === dm_coso_id);

    // Nhóm dữ liệu theo group
    const groups: Record<string, { id: number; text: string; groupName: string }[]> = {};
    filteredData.forEach(item => {
      const group = item.ten_nhom_mien_giam || 'Khác';
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push({
        id: item.id,
        text: item.ma_hinh_thuc,
        groupName: group
      });
    });

    // Tạo danh sách phẳng với dividers
    const result: any[] = [];
    const groupEntries = Object.entries(groups);

    groupEntries.forEach(([groupName, items], groupIndex) => {
      // Thêm divider trước group (trừ group đầu tiên)
      if (groupIndex >= 0) {
        result.push({
          id: `divider-${groupName}`,
          text: `Nhóm ${groupName}`,
          isDivider: true,
          disabled: true
        });
      }

      // Thêm các items của group
      result.push(...items);
    });

    return result;
  }, [props.sf_hinhthucmiengiams, dm_coso_id]);

  const filteredItems = useMemo(() => {
    if (!filter) return allSelectableItems;

    const normalizedFilter = removeVietnameseTones(filter.toLowerCase());
    return allSelectableItems.filter(item =>
      !item.isDivider && (
        removeVietnameseTones(item.text.toLowerCase()).includes(normalizedFilter) ||
        removeVietnameseTones(item.groupName?.toLowerCase() || '').includes(normalizedFilter)
      )
    );
  }, [allSelectableItems, filter]);

  // Tạo selected data
  const _selectedData = useMemo(() => {
    return allSelectableItems.filter(item =>
      !item.isDivider && props.value?.includes(item.id)
    );
  }, [props.value, allSelectableItems]);



  return (
    <SelectPanel
      renderAnchor={({ "aria-labelledby": ariaLabelledBy, ...anchorProps }) => (
        <Button
          sx={{
            width: "100%",
            display: "flex",
            height: "auto",
            justifyContent: "space-between",
            backgroundColor: "transparent",
            border: "none",
            cursor: "pointer",
            padding: 0,
          }}
          aria-labelledby={ariaLabelledBy}
          {...anchorProps}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              alignItems: "center",
              maxWidth: 400,
            }}
          >
            {_selectedData.map((item) => (
              <Label
                key={item.id}
                size="small"
                style={{ fontSize: 12 }}
                sx={{
                  backgroundColor: "canvas.subtle",
                  fontWeight: 400,
                  paddingX: 2,
                  paddingY: 1,
                  borderRadius: "20px",
                  fontSize: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Box sx={{ color: "accent.fg" }}>•</Box> {item.text}
              </Label>
            ))}
            <Button variant="invisible">
              <PencilIcon size={16} />
            </Button>
          </Box>
        </Button>
      )}
      title="Chọn hình thức miễn giảm"
      placeholderText="Tìm kiếm..."
      open={open}
      onOpenChange={setOpen}
      onFilterChange={setFilter}
      showItemDividers={true}
      overlayProps={{ width: "large", height: "medium" }}
      items={filter ? filteredItems : allSelectableItems}
      selected={_selectedData}
      onSelectedChange={(selectedItems: any[]) => {
        if (selectedItems.length === 0) {
          props.onValueChanged([]);
          return;
        }

        // Sử dụng Map để chỉ giữ lại mục cuối cùng của mỗi group
        const latestPerGroup = new Map<string, any>();

        // Duyệt từ cuối lên đầu để ưu tiên mục chọn sau cùng
        for (let i = selectedItems.length - 1; i >= 0; i--) {
          const item = selectedItems[i];
          const selectedItem = allSelectableItems.find(x => x.id === item.id && !x.isDivider);
          if (selectedItem && selectedItem.groupName && !latestPerGroup.has(selectedItem.groupName)) {
            latestPerGroup.set(selectedItem.groupName, item);
          }
        }

        const uniqueItems = Array.from(latestPerGroup.values());
        const newValues = uniqueItems.map((item) => item.id);
        props.onValueChanged(newValues);
      }}
    />
  );
};

export default SelectBoxMultipleHinhThucMienGiam;
